'use client';

import { useState } from 'react';
import { Alert, Button, DatePicker, Flex, Modal, Typography } from 'antd';
import { DownloadOutlined, EyeOutlined, SyncOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ManualUploadCenter } from '~/app/components/manualUpload/ManualUploadCenter';
import type { UseAssetLogsResult } from '~/app/hooks/assets/useAssetLogs';
import { useUpdateAssetLog } from '~/app/hooks/assets/useUpdateAssetLog';
import { useNormalizeAssetLogs } from '~/app/hooks/assets/useNormalizeAssetLogs';
import { AssetBareLogsPreviewModal } from './AssetBareLogsPreviewModal';
import { AssetLogsTable } from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable';
import styles from '~/app/components/asset-detail/AssetDetailLogsTab.module.css';

const { Text } = Typography;

type Props = {
  organizationId: string;
  assetId: string;
  assetName: string;
  clientName: string;
  assetLogs: UseAssetLogsResult;
  canDownloadBareLogs?: boolean;
};

export function AssetDetailLogsTab({
  organizationId,
  assetId,
  assetName,
  clientName,
  assetLogs,
  canDownloadBareLogs = false,
}: Props) {
  const { t } = useTranslation('assets');
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isNormalizeDialogOpen, setIsNormalizeDialogOpen] = useState(false);
  const [isBareLogsPreviewOpen, setIsBareLogsPreviewOpen] = useState(false);
  const assetLogUpdater = useUpdateAssetLog(organizationId, assetId);
  const assetLogNormalizer = useNormalizeAssetLogs(organizationId, assetId);
  const [normalizeDateRange, setNormalizeDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(
    assetLogs.filters.from && assetLogs.filters.to
      ? [dayjs(assetLogs.filters.from), dayjs(assetLogs.filters.to)]
      : [dayjs().subtract(1, 'month'), dayjs()],
  );

  const handleNormalize = async () => {
    if (!normalizeDateRange?.[0] || !normalizeDateRange?.[1]) return;

    await assetLogNormalizer.normalizeLogs({
      clientName,
      fromDate: normalizeDateRange[0].toISOString(),
      toDate: normalizeDateRange[1].toISOString(),
    });
    setIsNormalizeDialogOpen(false);
  };

  return (
    <>
      <div className={styles.logsInfiniteScrollTab}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Alert
            type="info"
            showIcon
            banner
            style={{
              flex: 1,
              minWidth: 240,
              borderRadius: 10,
              border: '1px solid var(--border-subtle)',
              background: 'var(--ant-colorFillAlter)',
            }}
            message={
              <Text type="secondary">{t('detail.logs.description')}</Text>
            }
          />
          <Flex gap={8} wrap="wrap">
            {canDownloadBareLogs ? (
              <Button
                icon={<EyeOutlined />}
                onClick={() => setIsBareLogsPreviewOpen(true)}
              >
                {t('detail.logs.actions.previewBareLogs')}
              </Button>
            ) : null}
            <Button
              icon={<SyncOutlined />}
              loading={assetLogNormalizer.isPending}
              onClick={() => setIsNormalizeDialogOpen(true)}
            >
              {t('detail.logs.actions.normalizeLogs')}
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => setIsImportDialogOpen(true)}
            >
              {t('detail.logs.actions.openImportDialog')}
            </Button>
          </Flex>
        </Flex>

        <div className={styles.logsInfiniteScrollTableHost}>
          <AssetLogsTable
            assetLogs={assetLogs}
            isUpdatingLog={assetLogUpdater.isPending}
            onUpdateLog={(logId, payload) =>
              assetLogUpdater.updateLog({ logId, payload })
            }
          />
        </div>
      </div>

      <Modal
        open={isImportDialogOpen}
        title={t('detail.logs.importDialog.title', { assetName })}
        onCancel={() => setIsImportDialogOpen(false)}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <Text type="secondary">
          {t('detail.logs.importDialog.description')}
        </Text>
        <div style={{ marginTop: 16 }}>
          <ManualUploadCenter
            key={assetId}
            initialAssetId={assetId}
            initialClientName={clientName}
            assetName={assetName}
            hideAssetField
          />
        </div>
      </Modal>

      <Modal
        open={isNormalizeDialogOpen}
        title={t('detail.logs.actions.normalizeLogs')}
        onCancel={() => setIsNormalizeDialogOpen(false)}
        onOk={handleNormalize}
        confirmLoading={assetLogNormalizer.isPending}
        okText={t('detail.logs.actions.normalizeLogs')}
      >
        <Flex vertical gap={16} style={{ padding: '16px 0' }}>
          <Text type="secondary">
            {t('detail.logs.normalizeDialog.description')}
          </Text>
          <DatePicker.RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            value={normalizeDateRange}
            onChange={(dates) => setNormalizeDateRange(dates as [Dayjs, Dayjs])}
            style={{ width: '100%' }}
          />
        </Flex>
      </Modal>

      {canDownloadBareLogs ? (
        <AssetBareLogsPreviewModal
          organizationId={organizationId}
          assetId={assetId}
          open={isBareLogsPreviewOpen}
          initialFrom={assetLogs.filters.from}
          initialTo={assetLogs.filters.to}
          onClose={() => setIsBareLogsPreviewOpen(false)}
        />
      ) : null}
    </>
  );
}
